import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box, Paper, Typography, TextField, Button, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Grid,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import debounce from 'lodash/debounce';
import { adminService } from '../services/admin';
import { useAuth } from '../context/AuthContext';
import { getToken, decodeToken } from '../utils/auth';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';

const EMPTY_FILTERS = { appName: '', appType: '' };

// Applications list for the logged-in business only. Same layout as the
// admin Applications page, minus the business filter/columns and every
// action except delete.
const BusinessApplications = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { user } = useAuth();
  // The business login token carries the business's own _id as `id`; fall
  // back to it in case the cached user object doesn't have one.
  const businessId = user?.id || decodeToken(getToken())?.id;

  const [applications, setApplications] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterInputs, setFilterInputs] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const filterInputsRef = useRef(filterInputs);
  filterInputsRef.current = filterInputs;

  const debouncedCommitFilters = useCallback(
    debounce(() => {
      setPage(0);
      setFilters({ ...filterInputsRef.current });
    }, 500),
    []
  );
  useEffect(() => () => debouncedCommitFilters.cancel(), [debouncedCommitFilters]);

  const handleNameChange = (e) => {
    const value = e.target.value;
    setFilterInputs((prev) => ({ ...prev, appName: value }));
    debouncedCommitFilters();
  };

  // Type is a single discrete pick — commit immediately, no debounce.
  const handleTypeChange = (e) => {
    const value = e.target.value;
    debouncedCommitFilters.cancel();
    const next = { ...filterInputsRef.current, appType: value };
    setFilterInputs(next);
    setPage(0);
    setFilters(next);
  };

  const handleClearFilters = () => {
    debouncedCommitFilters.cancel();
    setFilterInputs(EMPTY_FILTERS);
    setPage(0);
    setFilters(EMPTY_FILTERS);
  };

  const fetchApplications = async (isInitial = false) => {
    if (!businessId) return;
    try {
      isInitial ? setInitialLoading(true) : setTableLoading(true);
      const res = await adminService.getOwnBusinessApps(businessId, {
        page: page + 1,
        limit: rowsPerPage,
        appName: filters.appName || undefined,
        appType: filters.appType || undefined,
      });
      setApplications(res.data || []);
      setTotalRows(res.total || 0);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setSnackbar({ open: true, message: 'Failed to fetch applications', severity: 'error' });
    } finally {
      if (isInitial) setInitialLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (!businessId) {
      // Don't spin forever if we can't tell which business this is.
      setInitialLoading(false);
      setTableLoading(false);
      setSnackbar({ open: true, message: 'Could not identify your business — please log in again', severity: 'error' });
      return;
    }
    fetchApplications(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    if (!initialLoading) fetchApplications(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filters]);

  const handleRefresh = () => {
    fetchApplications(false);
    setSnackbar({ open: true, message: 'Applications refreshed', severity: 'success' });
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      setActionLoading(true);
      await adminService.deleteBusinessApp(deleteDialog._id);
      setSnackbar({ open: true, message: 'Application deleted', severity: 'success' });
      setDeleteDialog(null);
      // Step back a page if that was the last row on this one.
      if (applications.length === 1 && page > 0) setPage(page - 1);
      else await fetchApplications(false);
    } catch (err) {
      console.error('Failed to delete application:', err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to delete application',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    { field: 'appName', headerName: 'App Name', sortable: true },
    { field: 'appType', headerName: 'Type', sortable: true },
    {
      field: 'allowedDomains',
      headerName: 'Domains',
      render: (_, app) => {
        const count = Array.isArray(app.allowedDomains) ? app.allowedDomains.length : 0;
        return (
          <Chip
            label={count}
            size="small"
            sx={{
              bgcolor: isDark ? alpha(theme.palette.text.primary, 0.12) : '#f1f5f9',
              color: theme.palette.text.primary,
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'center',
      render: (_, app) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="Edit Application">
            <span>
              <IconButton size="small" onClick={() => navigate(`/business/applications/${app._id}/edit`)} sx={{ color: '#f59e0b' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete Application">
            <span>
              <IconButton size="small" onClick={() => setDeleteDialog(app)} sx={{ color: '#ef4444' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const hasActiveFilters = Object.values(filterInputs).some((v) => v);

  if (initialLoading) return <LoadingSpinner />;

  return (
    <Box sx={{ minHeight: '100%', background: theme.palette.background.default, p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Applications
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Manage your applications
          </Typography>
        </Box>
        <Tooltip title="Refresh Table">
          <IconButton
            onClick={handleRefresh}
            disabled={tableLoading}
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': { backgroundColor: theme.palette.action.hover },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
          boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.4)' : '0 1px 3px rgba(15, 23, 42, 0.08)',
          p: 3,
        }}
      >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="biz-app-type-filter-label">Type</InputLabel>
              <Select
                labelId="biz-app-type-filter-label"
                label="Type"
                value={filterInputs.appType}
                onChange={handleTypeChange}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="web">Web Application</MenuItem>
                <MenuItem value="mobile">Mobile Application</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="App Name"
              placeholder="App Name"
              value={filterInputs.appName}
              onChange={handleNameChange}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#94a3b8' }} /> }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {hasActiveFilters ? (
            <Button size="small" onClick={handleClearFilters} sx={{ textTransform: 'none' }}>
              Clear filters
            </Button>
          ) : <Box />}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/business/applications/create')}
            sx={{ background: '#2563EB', fontWeight: 600, textTransform: 'none' }}
          >
            Add Application
          </Button>
        </Box>

        <Box sx={{ position: 'relative' }}>
          {tableLoading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isDark ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255,255,255,0.7)', zIndex: 1 }}>
              <LoadingSpinner size={40} />
            </Box>
          )}
          <DataTable
            columns={columns}
            data={applications}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRows}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      </Paper>

      <Dialog open={!!deleteDialog} onClose={() => !actionLoading && setDeleteDialog(null)}>
        <DialogTitle>Delete Application</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{deleteDialog?.appName}</strong>? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} disabled={actionLoading}>Cancel</Button>
          <Button color="error" onClick={handleDelete} disabled={actionLoading}>
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessApplications;
